import React from 'react';

import { defineMessage, useIntl } from 'react-intl';

import { cssMap } from '@atlaskit/css';
import FieldIcon from '@atlaskit/icon/core/field';
import ProjectIcon from '@atlaskit/icon/core/project';
import WorkItemIcon from '@atlaskit/icon/core/work-item';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives
import Inline from '@atlaskit/primitives/inline';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives
import Stack from '@atlaskit/primitives/stack';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives
import Text from '@atlaskit/primitives/text';
import { token } from '@atlaskit/tokens';
import type { JiraEntities } from '@atlassian/ai-mate-jira-admin-agent-api/jira-admin-agent';

const fieldLabel = defineMessage({
	id: 'ai-mate-jira-admin-agent.jira-admin-agent.action-cards.entities.fieldLabel.non-final',
	defaultMessage: 'Field',
	description: 'Label of the section listing the fields',
});

const spaceLabel = defineMessage({
	id: 'ai-mate-jira-admin-agent.jira-admin-agent.action-cards.entities.spaceLabel.non-final',
	defaultMessage: 'Space',
	description: 'Label of the section listing the spaces',
});

const workTypeLabel = defineMessage({
	id: 'ai-mate-jira-admin-agent.jira-admin-agent.action-cards.entities.workTypeLabel.non-final',
	defaultMessage: 'Work Type',
	description: 'Label of the section listing the work types',
});

const styles = cssMap({
	fieldWrapper: {
		paddingTop: token('space.150'),
		paddingBottom: token('space.150'),
	},
});

export type EntitiesProps = {
	entities?: JiraEntities;
};

export const Entities = ({ entities }: EntitiesProps): React.JSX.Element => {
	const { formatMessage } = useIntl();
	return (
		<Stack>
			{entities?.fields && entities.fields.length > 0 && (
				<Stack xcss={styles.fieldWrapper}>
					<Text color="color.text.subtlest">{formatMessage(fieldLabel)}</Text>
					{entities.fields.map((field) => (
						<Inline key={field.id} space="space.100">
							<FieldIcon label="" />
							<Text>{field.name}</Text>
						</Inline>
					))}
				</Stack>
			)}
			{entities?.spaces && entities.spaces.length > 0 && (
				<Stack xcss={styles.fieldWrapper}>
					<Text color="color.text.subtlest">{formatMessage(spaceLabel)}</Text>
					{entities.spaces.map((space) => (
						<Inline key={space.id} space="space.100">
							<ProjectIcon label="" />
							<Text>{space.name}</Text>
						</Inline>
					))}
				</Stack>
			)}
			{entities?.workTypes && entities.workTypes.length > 0 && (
				<Stack xcss={styles.fieldWrapper}>
					<Text color="color.text.subtlest">{formatMessage(workTypeLabel)}</Text>
					{entities.workTypes.map((workType) => (
						<Inline key={workType.id} space="space.100">
							<WorkItemIcon label="" />
							<Text>{workType.name}</Text>
						</Inline>
					))}
				</Stack>
			)}
		</Stack>
	);
};
